import {
  ArrowRed,
  Box,
  calculatePercentageChange,
  Disclaimer,
  Div,
  GroupByType,
  H3,
  H5,
  Icon,
  lightTheme,
  P,
  Pill,
  Provider,
  NumberType,
  VDriveProvider,
  formatAmount,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { GraphWrapper } from './charts.style';
import { ChartTooltip } from './ChartTooltip';
import { CustomXAxisTick } from './CustomXAxisTick';
import { CustomYAxisTick } from './CustomYAxisTick';

// Types
export type ChartType = 'bar' | 'line' | 'area';

export type AllProvidersModal = {
  amount: number;
  label: string;
  byProvider: {
    provider: Provider | VDriveProvider;
    amount: number;
    amountToCompare?: number;
  }[];
  color: string;
  currency: NumberType;
  amountToCompare?: number;
  isComparing?: boolean;
  isToday?: boolean;
};

export interface BaseChartDataItem {
  key: string;
  [key: string]: string | number;
}

export interface ChartDataField {
  dataKey: string;
  color: string;
  strokeWidth?: number;
  fillOpacity?: number;
  strokeOpacity?: number;
  radius?: [number, number, number, number];
  stackId?: string;
  name?: string;
  /**
   * Render this Area as a primary filled series (solid fill, no stroke, no
   * dash) regardless of its index in `chartFields`. Default behavior keeps
   * index-0 as primary and dashes everything after it — used for compare
   * overlays. Set this when you genuinely have multiple primary series on
   * the same chart (e.g. profit + loss on a diverging Net Profit (P&L)).
   */
  solid?: boolean;
}

export interface ProviderData<T extends BaseChartDataItem> {
  provider: Provider;
  data: T[];
  totalCount: number;
}

export interface ModalData {
  provider: Provider;
  amount: number;
  amountToCompare: number;
}

export interface ReusableChartProps<T extends BaseChartDataItem> {
  // Chart configuration
  isComparing?: boolean;
  chartType: ChartType;
  title: string;
  titleCompare?: string;
  totalValue: number;
  totalValueToCompare?: number;
  valueColor?: string;
  currency: NumberType;

  // Data
  // data: T[];
  data: any;
  dataToCompare?: any;
  // dataByProvider?: ProviderData<T>[];
  dataByProvider?: any;
  dataByProviderToCompare?: any;
  groupByType: GroupByType;

  // Chart fields configuration
  chartFields: ChartDataField[];
  chartFieldsToCompare?: ChartDataField[];
  // Modal configuration
  setAllProvidersModal?: Dispatch<SetStateAction<AllProvidersModal | null>>;
  modalLabel?: string;
  // Custom arrow (top-right) that opens its own modal instead of the providers modal
  customArrow?: React.ReactNode;

  // Optional configurations
  showGrid?: boolean;
  showTooltip?: boolean;
  customMargins?: {
    top?: number;
    right?: number;
    left?: number;
    bottom?: number;
  };

  // Chart specific props
  fontSize?: string;

  // Custom components (optional overrides)
  customXAxisTick?: React.ComponentType<any>;
  customYAxisTick?: React.ComponentType<any>;
  customTooltip?: React.ComponentType<any>;
  customComponent?: React.ReactNode;
  // When provided, overrides the default formatAmount(totalValue, currency)
  // rendering in the header — use to inject combined metrics like "$X / N".
  customTotalValue?: React.ReactNode;

  // Chart height
  chartHeight?: string;
  chartHeightSM?: string;
}

export const renderPercentageChange = (
  calculatedPercentage: string,
  isInverted: boolean
) => {
  const color = () => {
    if (calculatedPercentage === '+0.0%') {
      return lightTheme.green;
    }
    if (!calculatedPercentage.includes('-')) {
      return isInverted ? lightTheme.primary : lightTheme.green;
    }
    return isInverted ? lightTheme.green : lightTheme.primary;
  };

  return (
    <Pill $background={`${color()}26`}>
      <Disclaimer $mb="0" $color={color()}>
        {calculatedPercentage}
      </Disclaimer>
    </Pill>
  );
};

export const ReusableChart = <T extends BaseChartDataItem>({
  isComparing,
  chartType,
  title,
  titleCompare,
  totalValue,
  totalValueToCompare,
  valueColor = lightTheme.green,
  currency,
  data = [],
  dataToCompare = [],
  dataByProvider = [],
  dataByProviderToCompare = [],
  groupByType,
  chartFields,
  chartFieldsToCompare,
  setAllProvidersModal,
  modalLabel,
  customArrow,
  showGrid = true,
  showTooltip = true,
  customMargins = {
    top: 20,
    right: 20,
    left: 0,
    bottom: 0,
  },
  fontSize = '14px',
  customXAxisTick: CustomXAxisTickComponent = CustomXAxisTick,
  customYAxisTick: CustomYAxisTickComponent = CustomYAxisTick,
  customTooltip: CustomTooltipComponent = ChartTooltip,
  customComponent,
  customTotalValue,
  chartHeight = '300px',
  chartHeightSM = '300px',
}: ReusableChartProps<T>) => {
  const [containerWidth, setContainerWidth] = useState(0);

  // Determine data to display
  const dataToShow = isComparing ? dataToCompare : data;

  const dataByProviderToShow = isComparing
    ? dataByProviderToCompare
    : dataByProvider;

  const chartFieldsToShow = isComparing ? chartFieldsToCompare : chartFields;

  const pixelsPerTick = 70; // ~width needed per tick
  const dynamicInterval = Math.max(
    0,
    Math.ceil((dataToShow.length * pixelsPerTick) / containerWidth)
  );

  // Prepare modal data
  const modalData: ModalData[] = dataByProviderToShow.map((d: any) => ({
    provider: d.provider,
    amount: d.totalValue,
    amountToCompare: d.totalValueToCompare,
  }));

  // Handle modal opening
  const handleModalOpen = () => {
    if (setAllProvidersModal && modalLabel) {
      setAllProvidersModal({
        amount: totalValue,
        amountToCompare: totalValueToCompare,
        label: modalLabel,
        byProvider: modalData,
        currency,
        color: valueColor,
        isComparing,
      });
    }
  };

  // Render chart based on type
  const renderChart = () => {
    const commonProps = {
      data: dataToShow,
      margin: customMargins,
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            {showGrid && dataToShow?.length > 0 && (
              <CartesianGrid strokeDasharray="2 2" />
            )}
            {renderAxes()}
            {showTooltip && renderTooltip()}
            {chartFieldsToShow?.map((field, index) => (
              <Line
                key={field.dataKey || index}
                type="monotone"
                dataKey={field.dataKey}
                stroke={field.color}
                strokeWidth={field.strokeWidth || 2}
                fillOpacity={field.fillOpacity || 1}
                strokeOpacity={field.strokeOpacity || 1}
                // animationBegin={600}
              />
            ))}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            {showGrid && dataToShow?.length > 0 && (
              <CartesianGrid strokeDasharray="2 2" />
            )}
            {renderAxes()}
            {showTooltip && renderTooltip()}
            {chartFieldsToShow?.map((field, index) => {
              const isPrimary = index === 0 || field.solid;
              return (
                <Area
                  key={field.dataKey || index}
                  type="monotone"
                  dataKey={field.dataKey}
                  stroke={isPrimary ? 'none' : field.color}
                  strokeWidth={1}
                  fillOpacity={isPrimary ? field.fillOpacity : 0.3}
                  strokeOpacity={field.strokeOpacity || 1}
                  opacity={isPrimary ? 0.8 : 1}
                  stackId={field.stackId}
                  fill={isPrimary ? field.color : 'transparent'}
                  strokeDasharray={isPrimary ? '0' : '5 5'}
                />
              );
            })}
          </AreaChart>
        );

      case 'bar':
      default:
        return (
          <BarChart {...commonProps}>
            {showGrid && dataToShow?.length > 0 && (
              <CartesianGrid strokeDasharray="2 2" />
            )}
            {renderAxes()}
            {showTooltip && renderTooltip()}
            {chartFieldsToShow?.map((field, index) => (
              <Bar
                key={field.dataKey || index}
                type="monotone"
                dataKey={field.dataKey}
                stroke={field.color}
                strokeWidth={field.strokeWidth || 2}
                strokeOpacity={field.strokeOpacity || 1}
                fillOpacity={field.fillOpacity || 1}
                fill={field.color}
                radius={field.radius || [0, 0, 0, 0]}
                stackId={field.stackId}
                // animationBegin={600}
              />
            ))}
          </BarChart>
        );
    }
  };

  const renderAxes = () => (
    <>
      <XAxis
        allowDataOverflow
        dataKey="key"
        fontSize={fontSize}
        interval={dynamicInterval}
        tick={(props) => (
          <CustomXAxisTickComponent
            {...props}
            groupByType={groupByType}
            interval="preserveStartEnd"
          />
        )}
      />
      <YAxis
        tick={
          <CustomYAxisTickComponent
            x={0}
            y={0}
            payload={{ value: 0 }}
            currency={currency}
          />
        }
        fontSize={fontSize}
      />
    </>
  );

  const renderTooltip = () => (
    <Tooltip
      cursor={false}
      wrapperStyle={{ outline: 'none' }}
      content={(props) => (
        <CustomTooltipComponent
          groupByType={groupByType}
          label={props.label}
          payload={props.payload}
          textColor={valueColor}
          currency={currency}
          isInverted={isInverted}
        />
      )}
    />
  );

  const calculatedPercentage = calculatePercentageChange(
    totalValue || 0,
    totalValueToCompare || 0
  );

  const renderComparedToTotal = () => (
    <Disclaimer $mb="0" $mt="4" $color={lightTheme.lightGray}>
      {formatAmount(totalValueToCompare || 0, currency)} {titleCompare}
    </Disclaimer>
  );

  const isInverted =
    title === 'Total Cancelled' ||
    title === 'Total Failed' ||
    title === 'Average Delivery Time' ||
    title === 'Average Assignment Time' ||
    title === 'Operating Expenses' ||
    title === 'Order Loss' ||
    title === 'Delivery Loss';

  return (
    <Box $p="0" $pSM="0" $relative>
      {/* No Data State */}
      {dataToShow?.length === 0 && (
        <Div
          $absolute
          $top="50%"
          $left="50%"
          $transform="translate(-50%, -50%)"
        >
          <P $m="0">No Data Available.</P>
        </Div>
      )}

      {/* Options Menu */}
      {customArrow ? (
        <Div $absolute $right="20px" $top="20px" $rightSM="18px" $topSM="18px">
          {customArrow}
        </Div>
      ) : (
        setAllProvidersModal &&
        modalLabel && (
          <Div
            $absolute
            $right="20px"
            $top="20px"
            $rightSM="18px"
            $topSM="18px"
          >
            <Icon
              $pointer
              onClick={handleModalOpen}
              color={lightTheme.lightGray}
            >
              <ArrowRed />
            </Icon>
          </Div>
        )
      )}

      {/* Header.
          On mobile the card is ~80vw wide, so a headline like "-$618.60 / 70"
          plus the compare pill plus a "See Orders" button cannot share one
          line. There the button rides the title line instead of being centred
          against the whole block ($alignStartSM), the headline is pinned to a
          single line ($noWrap - it used to break after the minus sign), and
          the pill drops under it when the two no longer fit ($wrapSM). */}
      <Div $flex $alignCenter $between $width="100%" $alignStartSM>
        <Div
          $flex
          $alignCenter
          $gap="16px"
          $gapSM="8px"
          $p="16px 20px 0 20px"
          // Lets the (non-shrinking) headline overflow this block rather than
          // push the button off the right edge of the card.
          style={{ minWidth: 0 }}
        >
          <Div>
            <H5 $m="0">{title}</H5>
            <Div $flex $alignCenter $gap="8px" $wrapSM>
              <H3 $mb="0" $color={valueColor} $noWrap>
                {customTotalValue ?? formatAmount(totalValue, currency)}
              </H3>
              {isComparing && (
                <Div $mobile>
                  {renderPercentageChange(calculatedPercentage, isInverted)}
                </Div>
              )}
            </Div>
          </Div>

          {isComparing ? (
            <Div>
              <Div $desktop>
                {renderPercentageChange(calculatedPercentage, isInverted)}
              </Div>
              <Div $desktop>{renderComparedToTotal()}</Div>
            </Div>
          ) : null}
        </Div>
        {customComponent && (
          // 10px on mobile centers the button against the title line, which
          // starts 16px down (the header's own padding-top).
          <Div $mr="16" $mtSM="10" $flexNone>
            {customComponent}
          </Div>
        )}
      </Div>

      {isComparing && (
        <Div $mobile $p="0 20px 0 20px">
          {renderComparedToTotal()}
        </Div>
      )}

      {/* Chart */}
      <GraphWrapper $height={chartHeight} $heightSM={chartHeightSM}>
        {dataToShow?.length > 0 && (
          <ResponsiveContainer
            width="100%"
            height="100%"
            onResize={(w) => setContainerWidth(w)}
          >
            {renderChart()}
          </ResponsiveContainer>
        )}
      </GraphWrapper>
    </Box>
  );
};

export * from './charts.style';
